import { EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { BehaviorSubject, combineLatest, map, of, switchMap, take } from 'rxjs';
import { DevToolsRegistry } from '../../dev-tools';
import { takeWhileAlive } from '../../utils';
import { FeatureService } from '../feature-registry';
import { WidgetType } from './widget-manifest.interface';
import { DynamicComponentHelper } from '../../dynamic-components';
import { AccessService } from '../../access/access/access.service';
import { simpleAbilityCheck } from '@core/shared/ability';
import * as i0 from "@angular/core";
import * as i1 from "../feature-registry";
import * as i2 from "../../access/access/access.service";
import * as i3 from "../../dev-tools";
function buildEmptyTypeToManifestMap() {
  return {
    [WidgetType.Content]: [],
    [WidgetType.Dashboard]: []
  };
}
export let FeatureWidgetsRegistry = /*#__PURE__*/(() => {
  class FeatureWidgetsRegistry {
    constructor(features, access, injector, debug) {
      this.features = features;
      this.access = access;
      this.injector = injector;
      this.typeToWidgetManifestsMap$ = new BehaviorSubject(buildEmptyTypeToManifestMap());
      this.features.getLoadedFeatures().pipe(takeWhileAlive()).subscribe(activatedFeatures => {
        const typeToWidgetManifestsMap = buildEmptyTypeToManifestMap();
        activatedFeatures.forEach(feature => {
          const definition = feature.getFeatureFunctionalityCached();
          if (definition.widgets?.length > 0) {
            definition.widgets.forEach(manifest => typeToWidgetManifestsMap[manifest.type].push(manifest));
          }
        });
        Object.values(typeToWidgetManifestsMap).forEach(manifests => {
          manifests.sort((a, b) => b.priority - a.priority);
        });
        this.typeToWidgetManifestsMap$.next(typeToWidgetManifestsMap);
      });
      debug.exposeDebugFunction(['Features', 'Inspect Widgets: Content'], () => {
        return this.typeToWidgetManifestsMap$.pipe(map(registry => registry[WidgetType.Content]), take(1));
      });
      debug.exposeDebugFunction(['Features', 'Inspect Widgets: Dashboard'], () => {
        return this.typeToWidgetManifestsMap$.pipe(map(registry => registry[WidgetType.Dashboard]), take(1));
      });
    }
    getContentWidgets$(positionId, skipAccessCheck = false) {
      return combineLatest({
        manifests: this.typeToWidgetManifestsMap$.pipe(map(registry => registry[WidgetType.Content])),
        sessionInfo: this.access.sessionInfo$ // important to trigger recalculation when user session changes
      }).pipe(map(({
        manifests
      }) => {
        const manifestsForPosition = manifests.filter(manifest => manifest.positionIds.includes(positionId));
        if (skipAccessCheck) return manifestsForPosition;
        const allowedManifests = manifestsForPosition.filter(manifest => {
          return simpleAbilityCheck(this.access.role, this.access.abilities, manifest.simpleAbilityCheck);
        });
        return allowedManifests;
      }), switchMap(manifests => {
        return combineLatest(manifests.map(manifest => this.createRuntimeWidgetState(manifest)));
      }));
    }
    getDashboardWidgets$(dashboardId, skipAccessCheck = false) {
      return combineLatest({
        manifests: this.typeToWidgetManifestsMap$.pipe(map(registry => registry[WidgetType.Dashboard])),
        sessionInfo: this.access.sessionInfo$ // important to trigger recalculation when user session changes
      }).pipe(map(({
        manifests
      }) => {
        const manifestsForPosition = manifests.filter(manifest => manifest.dashboardIds.includes(dashboardId) || manifest.allowAnyDashboard);
        if (skipAccessCheck) return manifestsForPosition;
        const allowedManifests = manifestsForPosition.filter(manifest => {
          return simpleAbilityCheck(this.access.role, this.access.abilities, manifest.simpleAbilityCheck);
        });
        return allowedManifests;
      }), switchMap(manifests => {
        return combineLatest(manifests.map(manifest => this.createRuntimeWidgetState(manifest)));
      }));
    }
    createRuntimeWidgetState(manifest) {
      if (manifest === null) return of(null);
      if (manifest.type === WidgetType.Dashboard) {
        if (manifest.visibility) {
          runInInjectionContext(this.injector, () => {
            manifest.visibilityFnCached = manifest.visibility();
          });
        }
      }
      if (!manifest.componentInfo) {
        return DynamicComponentHelper.loadComponent(manifest.component || manifest.load || null).pipe(map(componentInfo => {
          manifest.componentInfo = componentInfo;
          return manifest;
        }));
      } else {
        return of(manifest);
      }
    }
    static {
      this.ɵfac = function FeatureWidgetsRegistry_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FeatureWidgetsRegistry)(i0.ɵɵinject(i1.FeatureService), i0.ɵɵinject(i2.AccessService), i0.ɵɵinject(i0.EnvironmentInjector), i0.ɵɵinject(i3.DevToolsRegistry));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: FeatureWidgetsRegistry,
        factory: FeatureWidgetsRegistry.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FeatureWidgetsRegistry;
})();